import { EZendeskCustomField, IZendeskCustomField, IZendeskTicket } from "@kuika/kuika-cl-model/src/models/zendesk";
import classNames from "classnames";
import { PureComponent } from "react";
import DateService from "../../../services/date-service";
import KuikaBadge from "../../ui-kit/kuika-badge/kuika-badge";
import { getTicketIdColor } from "./ticket-id-color";
import { stripMarkdownPreview } from "./ticket-preview-text";
import ZendeskStatusBadge, { ZendeskTicketStatus } from "./zendesk-status-badge";
import "./zendesk-ticket-item.scss";
import { getTicketDisplayStatus } from "./zendesk-ticket-filter";

interface Props {
  ticket: IZendeskTicket;
  active: boolean;
  /** Display name of the user who opened the ticket ("açan kişi"); shown next to the subject when set. */
  requesterName?: string;
  onClick?: (ticket: IZendeskTicket) => void;
}

interface State {
  isSelectedAtLeastOneTime: boolean;
}

class ZendeskTicketItem extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      isSelectedAtLeastOneTime: false
    };
  }

  componentDidMount(): void {
    if (this.props.active) {
      this.setState({ isSelectedAtLeastOneTime: true });
    }
  }

  private onClickTicket = (): void => {
    this.setState({ isSelectedAtLeastOneTime: true });
    this.props.onClick?.(this.props.ticket);
  };

  render() {
    const zendeskTicketItemClassNames: string = classNames({
      "zendesk-ticket-item": true,
      "zendesk-ticket-item--active": this.props.active
    });

    const notify: IZendeskCustomField | undefined = this.props.ticket.customFields.find(
      (f: IZendeskCustomField) => f.id === EZendeskCustomField.NOTIFY
    );
    const isUnread: boolean = !this.state.isSelectedAtLeastOneTime && Boolean(notify?.value);
    const status: ZendeskTicketStatus | undefined = getTicketDisplayStatus(this.props.ticket);
    // The clamped preview shows plain text: Markdown syntax such as "**Öncelik:**" would otherwise
    // leak into the row as literal asterisks. The ticket's own data, and its full-text viewer, are
    // untouched — this only reshapes what the 2-line clamp displays.
    const descriptionPreview: string = stripMarkdownPreview(this.props.ticket.description);

    return (
      <button className={zendeskTicketItemClassNames} onClick={this.onClickTicket}>
        <div className="zendesk-ticket-item--title-container">
          {isUnread && <span className="zendesk-ticket-item--notify-icon" />}
          <span data-testid="ticketItem" className="zendesk-ticket-item--title">
            {this.props.ticket.subject}
            {this.props.requesterName && (
              <span className="zendesk-ticket-item--requester"> ({this.props.requesterName})</span>
            )}
          </span>
          {/*
            The id gets its OWN hue, derived from the number itself — status already has a chip in
            this row, and colouring the id from it made a solved inbox one repeated colour. Quiet
            weight, so the solid status chip stays the loud one.
          */}
          <span className="zendesk-ticket-item--badge">
            <KuikaBadge
              content={`#${this.props.ticket.id}`}
              color={getTicketIdColor(this.props.ticket.id)}
              variant="subtle"
            />
          </span>
        </div>
        <div className="zendesk-ticket-item--last-message">{descriptionPreview}</div>
        <div className="zendesk-ticket-item--footer">
          {status && <ZendeskStatusBadge status={status} />}
          <span className="zendesk-ticket-item--relative-time">
            {DateService.getHumanizedDate(this.props.ticket.updatedAt)}
          </span>
        </div>
      </button>
    );
  }
}

export default ZendeskTicketItem;
